import { getBestReceiptOrder, getOrderPaymentMethodCode } from './orderUtils';

const normalizeToken = (value = '') => (
    String(value || '')
        .trim()
        .replace(/[\s-]+/g, '_')
        .toUpperCase()
);

const titleizeToken = (value = '') => (
    String(value || '')
        .trim()
        .replace(/[_-]+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
);

const buildMeta = (value, label, classes) => ({
    value,
    label,
    badgeClassName: `${classes.bg} ${classes.text} ${classes.border}`,
    helperText: classes.helperText || '',
    ...classes
});

const DEFAULT_META = buildMeta('UNKNOWN', 'Pending', {
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200'
});

const RECEIPT_STATUS_META = {
    PAID: buildMeta('PAID', 'Paid', {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200'
    }),
    CANCELLED: buildMeta('CANCELLED', 'Cancelled', {
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        border: 'border-slate-200'
    }),
    REFUNDED: buildMeta('REFUNDED', 'Refunded', {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200'
    }),
    PARTIALLY_REFUNDED: buildMeta('PARTIALLY_REFUNDED', 'Partially Refunded', {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200'
    }),
    REFUND_PENDING: buildMeta('REFUND_PENDING', 'Refund Pending', {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200'
    }),
    PENDING_CASH: buildMeta('PENDING_CASH', 'Pending Cash', {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200'
    }),
    PENDING_VERIFICATION: buildMeta('PENDING_VERIFICATION', 'Pending Verification', {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        helperText: 'Payment is complete. We are waiting for verification.'
    }),
    PAYMENT_PENDING: buildMeta('PAYMENT_PENDING', 'Payment Pending', {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200'
    }),
    PAYMENT_PROCESSING: buildMeta('PAYMENT_PROCESSING', 'Payment Processing', {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200'
    }),
    PROCESSING: buildMeta('PROCESSING', 'Processing', {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200'
    }),
    CREATED: buildMeta('CREATED', 'Created', {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200'
    }),
    SHIPPED: buildMeta('SHIPPED', 'Shipped', {
        bg: 'bg-sky-50',
        text: 'text-sky-700',
        border: 'border-sky-200'
    }),
    DELIVERED: buildMeta('DELIVERED', 'Delivered', {
        bg: 'bg-teal-50',
        text: 'text-teal-700',
        border: 'border-teal-200'
    }),
    OVERDUE: buildMeta('OVERDUE', 'Overdue', {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-200'
    }),
    PENDING: buildMeta('PENDING', 'Pending', {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        border: 'border-yellow-200'
    })
};

const CASH_PAYMENT_METHODS = new Set(['CASH', 'CASH_ON_DELIVERY', 'COD']);
const MANUAL_VERIFICATION_PAYMENT_METHODS = new Set([
    'ZELLE',
    'PAYMENT_APP',
    'BANK_ACCOUNT',
    'BANK_TRANSFER',
    'UPI',
    'QR_CODE'
]);

const COMPLETE_PAYMENT_STATUSES = new Set(['SUCCESS', 'COMPLETED', 'CAPTURED', 'PAID']);
const VERIFICATION_PENDING_PAYMENT_STATUSES = new Set(['PRE_AUTH', 'AUTHORIZED']);

const resolveMeta = (value, fallbackLabel = '') => {
    const normalizedValue = normalizeToken(value);
    if (RECEIPT_STATUS_META[normalizedValue]) {
        return RECEIPT_STATUS_META[normalizedValue];
    }

    if (!normalizedValue) {
        return DEFAULT_META;
    }

    return buildMeta(normalizedValue, fallbackLabel || titleizeToken(normalizedValue), {
        bg: DEFAULT_META.bg,
        text: DEFAULT_META.text,
        border: DEFAULT_META.border
    });
};

const getPrimaryTransaction = (order, invoice) =>
    order?.transactionList?.[0]
    || invoice?.order?.transactionList?.[0]
    || null;

const getReceiptPaymentMethodCode = (invoice, order) => {
    const bestOrder = getBestReceiptOrder(order, invoice?.order);
    const orderMethodCode = normalizeToken(getOrderPaymentMethodCode(bestOrder));
    if (orderMethodCode) {
        return orderMethodCode;
    }

    const primaryTransaction = getPrimaryTransaction(bestOrder, invoice);
    const paymentMethod = primaryTransaction?.paymentMethod || {};
    const paymentMethodName = normalizeToken(paymentMethod?.name);

    if (paymentMethodName.includes('ZELLE')) {
        return 'ZELLE';
    }

    return normalizeToken(paymentMethod?.paymentMethodType || invoice?.paymentMethod);
};

export const resolveReceiptStatus = (invoice, order) => {
    const bestOrder = getBestReceiptOrder(order, invoice?.order);
    const orderStatus = normalizeToken(bestOrder?.currentStatus || bestOrder?.status);
    const invoiceStatus = normalizeToken(invoice?.status);
    const paymentStatus = normalizeToken(
        invoice?.paymentStatus
        || getPrimaryTransaction(bestOrder, invoice)?.currentStatus
    );
    const paymentMethodCode = getReceiptPaymentMethodCode(invoice, bestOrder);
    const isCashPayment = CASH_PAYMENT_METHODS.has(paymentMethodCode);
    const requiresManualVerification = MANUAL_VERIFICATION_PAYMENT_METHODS.has(paymentMethodCode);
    const statuses = [orderStatus, invoiceStatus].filter(Boolean);

    if (statuses.includes('REFUNDED')) {
        return RECEIPT_STATUS_META.REFUNDED;
    }

    if (statuses.includes('PARTIALLY_REFUNDED')) {
        return RECEIPT_STATUS_META.PARTIALLY_REFUNDED;
    }

    if (statuses.includes('REFUND_PENDING')) {
        return RECEIPT_STATUS_META.REFUND_PENDING;
    }

    if (statuses.includes('CANCELLED')) {
        return RECEIPT_STATUS_META.CANCELLED;
    }

    if (statuses.includes('PAID')) {
        return RECEIPT_STATUS_META.PAID;
    }

    if (statuses.includes('PAYMENT_PROCESSING')) {
        return requiresManualVerification && !isCashPayment
            ? RECEIPT_STATUS_META.PENDING_VERIFICATION
            : RECEIPT_STATUS_META.PAYMENT_PROCESSING;
    }

    if (statuses.includes('PAYMENT_PENDING')) {
        return isCashPayment
            ? RECEIPT_STATUS_META.PENDING_CASH
            : RECEIPT_STATUS_META.PAYMENT_PENDING;
    }

    if (COMPLETE_PAYMENT_STATUSES.has(paymentStatus)) {
        return RECEIPT_STATUS_META.PAID;
    }

    if (VERIFICATION_PENDING_PAYMENT_STATUSES.has(paymentStatus)) {
        return requiresManualVerification && !isCashPayment
            ? RECEIPT_STATUS_META.PENDING_VERIFICATION
            : RECEIPT_STATUS_META.PROCESSING;
    }

    const fallbackStatus = orderStatus || invoiceStatus || paymentStatus;
    return resolveMeta(fallbackStatus);
};

export const formatReceiptPaymentStatus = (paymentStatus, invoice, order) => {
    const normalizedPaymentStatus = normalizeToken(paymentStatus);
    const resolvedStatus = resolveReceiptStatus(invoice, order);

    if (resolvedStatus.value === 'REFUNDED') {
        return 'Refunded';
    }

    if (resolvedStatus.value === 'PARTIALLY_REFUNDED') {
        return 'Partially Refunded';
    }

    if (resolvedStatus.value === 'REFUND_PENDING') {
        return 'Refund Pending';
    }

    if (resolvedStatus.value === 'CANCELLED') {
        return 'Cancelled';
    }

    if (resolvedStatus.value === 'PAID') {
        return 'Completed';
    }

    if (resolvedStatus.value === 'PENDING_VERIFICATION') {
        return 'Completed - Verification Pending';
    }

    if (resolvedStatus.value === 'PENDING_CASH') {
        return 'Pending Cash Payment';
    }

    if (resolvedStatus.value === 'PAYMENT_PENDING') {
        return 'Payment Pending';
    }

    if (normalizedPaymentStatus === 'REFUNDED') {
        return 'Refunded';
    }

    if (VERIFICATION_PENDING_PAYMENT_STATUSES.has(normalizedPaymentStatus)) {
        return 'Verification Pending';
    }

    if (COMPLETE_PAYMENT_STATUSES.has(normalizedPaymentStatus)) {
        return 'Completed';
    }

    return titleizeToken(paymentStatus || '');
};
